'use client';
import React, { useEffect, useRef } from 'react';
// import * as icons from './icons';
import styled from '@benzinga/themetron';
import classNames from 'classnames';
import Hooks from '@benzinga/hooks';
import { Button } from '@benzinga/core-ui';
import { BzImage } from '@benzinga/image';
import { ChevronDown } from '@benzinga/themed-icons';

// type MoneyServicesListIcon = keyof typeof icons;

export interface MoneyServiceListItemProps {
  title: string;
  icon: string;
  lists: {
    title: string;
    articles: {
      id: number;
      title: string;
      url: string;
    }[];
  }[];
}

interface Props {
  listItem: MoneyServiceListItemProps;
  isActive: boolean;
  index: number;
  onClick: (index: number) => void;
}

export const MoneyServiceListItem = ({ index, isActive, listItem, onClick }: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { width } = Hooks.useWindowSize();
  const isMobile = width && width < 568;
  const isTablet = width && width < 768;
  const [showMore, setShowMore] = React.useState(false);

  useEffect(() => {
    if (contentRef.current) {
      if (isActive) {
        contentRef.current.style.maxHeight = contentRef.current.scrollHeight + 'px';
      } else {
        contentRef.current.style.maxHeight = '0';
      }
    }
  }, [isActive, width, showMore]);

  const clickHandler = () => {
    if (!isActive) onClick(index);
  };

  return (
    <MoneyServiceListItemWrapper
      className={classNames('money-service-list-item-wrapper', { [`list-item-active`]: isActive })}
    >
      <div className={'list-item-header'} onClick={clickHandler}>
        {listItem.icon && <BzImage alt={listItem.title} height={32} src={listItem.icon} width={32} />}
        <p className="list-item-title">{listItem.title}</p>
        <ChevronDown className="list-item-chevron-icon" />
      </div>
      <div className="list-item-content" ref={contentRef}>
        {Array.isArray(listItem.lists) &&
          listItem.lists.map((list, i) => (
            <div
              className={`item-wrapper ${showMore} ${i} ${
                !showMore && ((isMobile && i > 1) || (isTablet && i > 3)) ? 'hide' : 'show'
              }`}
              key={i}
            >
              <div className="item-title">{list.title}</div>
              <ul className="list-links-wrapper">
                {Array.isArray(list.articles) &&
                  list.articles.map(link => (
                    <li className="list-link" key={link.id}>
                      <a href={link.url}>{link.title}</a>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
      </div>
      {(isTablet || isMobile) && isActive && (
        <Button as="div" className="list-action-button" onClick={() => setShowMore(!showMore)} size="md" variant="flat">
          {!showMore ? 'Show More' : 'Show Less'}
        </Button>
      )}
    </MoneyServiceListItemWrapper>
  );
};

const MoneyServiceListItemWrapper = styled.div`
  &.money-service-list-item-wrapper {
    border: 1px solid #e1ebfa;
    border-radius: 4px;
  }

  &.list-item-active {
    padding-bottom: 40px;

    .list-item-header {
      border-bottom: 1px solid #e1ebfa;
    }

    .list-item-chevron-icon {
      transform: scaleY(-1);
      fill: #3f83f8;
    }

    .list-item-content {
      margin-top: 16px;
    }

    @media screen and (max-width: 767px) {
      padding-bottom: 24px;
    }
  }

  &:not(.list-item-active):hover {
    cursor: pointer;
    .list-item-chevron-icon {
      fill: #3f83f8;
    }
  }

  .list-item-header {
    padding: 16px 24px;
    display: flex;
    align-items: center;
    gap: 24px;
    background-color: #f2f8ff;
  }

  .list-item-title {
    font-weight: 700;
    font-size: 18px;
    line-height: 32px;
    color: #192940;
    margin-bottom: 0;
  }

  .list-item-chevron-icon {
    margin-left: auto;
    fill: #5b7292;
    transition:
      0.25s fill ease-in-out,
      0.25s transform ease-in-out;
  }

  .list-item-content {
    margin-top: 0;
    will-change: max-height, margin-top;
    max-height: 0;
    overflow: hidden;
    transition:
      0.4s max-height ease-in-out,
      0.4s margin-top ease-in-out;
    display: grid;
    grid-auto-columns: 1fr;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 24px 16px;
    padding: 0 24px;

    @media screen and (max-width: 767px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: 567px) {
      grid-template-columns: 1fr;
    }
  }
  .item-wrapper {
    display: block;
    transition: 0.3s;
    &.hide {
      transition: 0.3s;
      display: none;
    }
  }

  .item-title {
    margin-bottom: 16px;
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
    color: #5b7292;
  }

  .list-links-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .list-link {
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: #3f83f8;
    display: flex;
    gap: 8px;
    transition: 0.25s color ease-in-out;

    &:hover {
      color: #3f83f890;
    }

    &:before {
      content: '•';
      color: currentColor;
    }
  }

  .list-action-button {
    display: block;
    width: calc(100% - 48px);
    margin: 16px auto 0;
    height: 36px;
    text-transform: uppercase;
    text-align: center;

    &:hover {
      color: #3f83f8;
      border-color: #3f83f8;
    }
  }
`;
